import type { AgentProgressEvent, AgentTimelineEntry } from '~/components/AiChat/AgentTimeline/types';

/** `metadata.kind` of every progress-timeline event the AI service publishes (FNOL, documents). */
export const PROGRESS_EVENT_KIND = {
  PLAN: 'plan',
  STEP_STARTED: 'step_started',
  STEP_PROGRESS: 'step_progress',
  STEP_FAILED: 'step_failed',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

export type ProgressEventKind = (typeof PROGRESS_EVENT_KIND)[keyof typeof PROGRESS_EVENT_KIND];

export interface PlanStep {
  index: number;
  key: string;
  title: string;
}

/**
 * `metadata` is an untyped bag on the envelope; these are the keys the timeline contract defines.
 * Row identity is always `step_key`, never the envelope's `step_id`.
 */
export interface ProgressMetadata {
  kind?: ProgressEventKind;
  steps?: PlanStep[];
  step_key?: string;
  index?: number;
  detail?: string;
}

export type ProgressRowStatus = NonNullable<AgentTimelineEntry['status']>;

export const PROGRESS_ROW_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  ERROR: 'error',
} as const satisfies Record<string, ProgressRowStatus>;

export interface ProgressStatus {
  text: string;
  /**
   * When this run started — the `plan` event's timestamp. A run still in flight has to count against
   * the clock rather than against its own last event, which would sit still between steps.
   */
  startedAt: string;
  /**
   * The run's settled span, first event to last. Only meaningful once it has finished; while it is in
   * flight the last event is wherever the run happens to have got to, not the current elapsed time.
   */
  durationSeconds: number | null;
  /** No terminal event yet, so the elapsed time is still climbing. */
  isRunning: boolean;
}

export type ProgressEventHandler<S, M extends ProgressMetadata = ProgressMetadata> = (
  state: S,
  event: AgentProgressEvent,
  meta: M
) => void;

export type ProgressEventHandlers<S, M extends ProgressMetadata = ProgressMetadata> = Partial<
  Record<ProgressEventKind, ProgressEventHandler<S, M>>
>;
